import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { useContent } from "../context/ContentContext";
import { useItinerary } from "../context/ItineraryContext";
import PageHero from "../components/PageHero";
import { AttractionsGrid } from "../components/AttractionsGrid";
import { cropStyle } from "../lib/cropStyle";

export default function Experiences() {
  const { content } = useContent();
  const { add } = useItinerary();
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [highlightId, setHighlightId] = useState(null);
  const ph = content?.page_heroes?.experiences || {};
  const cta = content?.exp_cta || {};

  useEffect(() => { api.get("/experiences").then((r) => setItems(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    const sp = new URLSearchParams(location.search);
    const exId = sp.get("exp");
    const q = sp.get("q");
    if ((!exId && !q) || !items.length) return;
    let match = null;
    if (exId) match = items.find((e) => e.id === exId);
    if (!match && q) {
      const ql = q.toLowerCase();
      const words = ql.split(/\s+/).filter((w) => w.length > 3);
      match = items.find((e) => {
        const h = `${e.title} ${e.category || ""} ${e.desc || ""}`.toLowerCase();
        return h.includes(ql) || words.some((w) => h.includes(w));
      });
    }
    if (match) {
      setHighlightId(match.id);
      setTimeout(() => {
        const el = document.getElementById(`exp-${match.id}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 300);
      const t = setTimeout(() => setHighlightId(null), 3200);
      return () => clearTimeout(t);
    }
  }, [items, location.search]);

  return (
    <div>
      <PageHero
        eyebrow={ph.eyebrow || "Experiences & Attractions"}
        title={ph.title || "Adventures beyond the retreat"}
        subtitle={ph.subtitle || "From casino evenings at Leopard Rock to speedboating at Osbourne Dam — we curate it all."}
        image={ph.image || "https://images.unsplash.com/photo-1569302911021-297d2362e3d3?q=85&w=1600"}
      />

      <section className="py-20 container-lux">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((e, i) => (
            <motion.div
              key={e.id} id={`exp-${e.id}`} data-testid={`experience-${e.id}`}
              initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
              className={`card-lux overflow-hidden flex flex-col transition-shadow duration-500 ${highlightId === e.id ? "ring-2 ring-rose ring-offset-2 shadow-xl" : ""}`}
            >
              <div className="h-52 overflow-hidden relative">
                <img src={e.image} alt={e.title} className="h-full w-full object-cover object-center hover:scale-105 transition-transform duration-500" style={cropStyle(e.image_position, e.image_zoom)} />
                <span className="absolute top-3 left-3 bg-forest/85 text-cream text-[11px] px-3 py-1 rounded-full tracking-wide">{e.category}</span>
              </div>
              <div className="p-6 flex flex-col flex-1">
                <h3 className="font-serif text-2xl text-forest">{e.title}</h3>
                <p className="text-sm text-stone-600 mt-3 leading-relaxed flex-1">{e.desc}</p>
                <div className="mt-5 flex items-center justify-between gap-2">
                  <span className="text-rose font-semibold">from ${e.price} <span className="text-stone-400 font-normal text-xs">pp</span></span>
                  <div className="flex items-center gap-2">
                    <button onClick={() => { add(e.id); toast.success(`${e.title} added to your itinerary`); }} data-testid={`exp-add-stay-${e.id}`} className="text-sm font-semibold rounded-full bg-rose/10 text-rose hover:bg-rose/20 px-3 py-1.5 transition-colors">+ Add</button>
                    <button onClick={() => navigate(`/booking?experience=${e.id}`)} data-testid={`exp-book-${e.id}`} className="text-sm font-semibold text-forest hover:text-rose transition-colors">Book →</button>
                  </div>
                </div>
                {e.gallery_category && (
                  <button onClick={() => navigate(`/gallery?cat=${encodeURIComponent(e.gallery_category)}${e.gallery_subcategory ? `&sub=${encodeURIComponent(e.gallery_subcategory)}` : ""}&exp=${e.id}`)} data-testid={`exp-gallery-${e.id}`} className="mt-3 text-xs font-semibold text-forest/70 hover:text-rose transition-colors">View {e.gallery_subcategory || e.gallery_category} photos →</button>
                )}
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {(content?.attractions || []).some((a) => a.experience_id) && (
        <section className="pb-6 container-lux">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <span className="eyebrow">Nearby</span>
            <h2 className="font-serif text-3xl sm:text-4xl text-forest mt-2">Build your itinerary</h2>
            <p className="text-sm text-stone-500 mt-3">Add nearby attractions straight to your reservation.</p>
          </div>
          <AttractionsGrid attractions={(content?.attractions || []).filter((a) => a.experience_id)} />
        </section>
      )}

      <section className="pb-24 container-lux">
        <div className="rounded-3xl bg-forest text-cream p-12 text-center">
          <span className="eyebrow">Bespoke packages</span>
          <h2 className="font-serif text-3xl sm:text-4xl mt-3 max-w-2xl mx-auto">{cta.title || "Let us design your perfect highland getaway"}</h2>
          <p className="text-cream/70 mt-4 max-w-xl mx-auto text-sm">{cta.subtitle || "Combine your stay with any experience — our concierge tailors every detail, from transfers to private guides."}</p>
          <button onClick={() => navigate("/booking")} className="btn-primary mt-8">Start Planning</button>
        </div>
      </section>
    </div>
  );
}
